import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import AIAssistantChat from './AIAssistantChat';
import ChatPersistenceSync from './ChatPersistenceSync';
import { Bot, X, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useChatStore } from '@/stores/chatStore';
import { useLocation } from 'wouter';
import { featureFlags } from '@/config/featureFlags';

interface AIAssistantFloatProps {
  className?: string;
}

export default function AIAssistantFloat({ className }: AIAssistantFloatProps) {
  const isOpen = useChatStore(s => s.isOpen);
  const isMinimized = useChatStore(s => s.isMinimized);
  const open = useChatStore(s => s.open);
  const close = useChatStore(s => s.close);
  const minimize = useChatStore(s => s.minimize);
  const maximize = useChatStore(s => s.maximize);
  const setRoute = useChatStore(s => s.setRoute);

  const [location] = useLocation();

  // Track current route for context-aware suggested prompts
  useEffect(() => {
    setRoute(location);
  }, [location, setRoute]);

  const handleToggle = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  if (!featureFlags.aiAssistant) return null;

  return (
    <div className={cn('fixed bottom-6 right-6 z-50', className)}>
      {/* Persistence sync (renderless) */}
      <ChatPersistenceSync />

      {/* Chat Window */}
      {isOpen && (
        <div className="animate-in slide-in-from-bottom-5 duration-300">
          <div className="relative">
            <AIAssistantChat
              isMinimized={isMinimized}
              onMinimize={minimize}
              onMaximize={maximize}
              className="shadow-2xl border border-slate-200 bg-white"
            />
            {/* Close button for maximized chat */}
            {!isMinimized && (
              <Button
                variant="ghost"
                size="sm"
                onClick={close}
                className="absolute -top-2 -right-2 h-6 w-6 p-0 bg-white border shadow-sm hover:bg-slate-50"
                data-testid="button-close-chat"
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      {!isOpen && (
        <div className="relative">
          <Button
            onClick={handleToggle}
            size="lg"
            className="h-14 w-14 rounded-2xl border border-white bg-brand-red shadow-[0_20px_45px_-22px_rgba(225,5,0,0.75)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-red-700 hover:shadow-[0_24px_55px_-24px_rgba(225,5,0,0.85)] active:translate-y-0 active:scale-[0.98]"
            data-testid="button-ai-assistant-toggle"
          >
            <div className="relative">
              <Bot className="w-6 h-6 text-white" />
              <Sparkles className="absolute -right-1 -top-1 h-3 w-3 text-red-50" />
            </div>
          </Button>

          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className="bg-slate-900 bg-opacity-95 text-white text-sm px-3 py-2 rounded-lg shadow-xl whitespace-nowrap backdrop-blur-sm">
              Agent Command Layer
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
